/**
 * changeOrderSerializer — payload builder for the change-order write path.
 *
 * Governed by PT-53A W1.4c · response §8 architecture:
 *
 *   "Two thin serializers (`serializeQuoteLine`, `serializeChangeOrderLine`)
 *    both call the shared line-level mapper. Each owns only its collection
 *    key and envelope-specific fields."
 *
 * Prior to this, `ChangeOrderBuilderPage.handleSave` and `.handleFinalize`
 * sent raw camelCase builder state as `{ jobId, lines: coLines, ... }` (DEF-086
 * and DEF-096). Zero fields were mapped to the snake_case display contract
 * that ChangeOrderDetail / ChangeOrderReviewSend read, so line-level money
 * fields silently dropped on round-trip.
 *
 * Envelope key stays `lines` (not `line_items`) — that is the existing
 * write contract with the backend; renaming is out of scope for W1.
 *
 * CO-only line-level fields preserved through serialization:
 *   • `quoteLineId`   — link back to the quote line the CO delta is against
 *   • `alternateOf`   — original alt-group anchor
 *   • `status`        — 'unchanged' | 'edited' | 'added' | 'removed'
 *   • `locked`        — whether the row is finalized
 *   • `original`      — snapshot of the pre-change quote line (LF-08
 *                       critical: if this doesn't survive the round-trip,
 *                       CO#2 re-seeds against reloaded state and silently
 *                       reverts CO#1)
 *   • `id`, `areaId`, `areaLabel`, `isCustom`, `description`, `unit`,
 *     `unitPrice` — passed through for the builder's own rehydrate path.
 */

import { mapLineFields } from '../lib/serializers/lineFieldMapper';

/**
 * Serialize a single CO working line. Emits the six-field snake_case display
 * contract via the shared mapper AND preserves the CO-only fields listed in
 * the module docstring.
 *
 * Unlike quote lines, CO lines don't receive a separate `computed` argument —
 * IDI-454: this comment used to claim "the working `lineTotal` in state IS
 * the resolved total". That is FALSE for an ADDED line: `buildLineItem`
 * creates it with `lineTotal: null` (the quote builder's margin engine fills
 * that in, and the CO builder has no equivalent). The CO engine's computed
 * total lives on `computeChangeOrder().lines`, not on the lines held in
 * state, so nothing ever wrote it back. The payload then carried
 * `lineTotal: null` AND `line_total: 0` — two keys contradicting each other —
 * and the change order billed $0.
 *
 * So the engine-computed line is now passed in and is the source of truth
 * for the price, exactly as the quote path passes `useQuoteMargins`'s
 * `computedLines[i]`.
 *
 * @param {object} coLine    Builder-native CO line.
 * @param {object=} computed Engine-computed line from `computeChangeOrder`
 *                           ({ total }). Required for a correct price on an
 *                           added line.
 * @returns {object}         Serialized line for the wire.
 */
export function serializeChangeOrderLine(coLine, computed = null) {
  if (!coLine || typeof coLine !== 'object') return coLine;

  const mapped = mapLineFields(coLine, computed);

  return {
    // Preserve builder-native keys so the builder can rehydrate on reload.
    // (Same pattern as serializeLineItem in quotePrefill.)
    ...coLine,

    // Snake_case display contract from the shared mapper.
    ...mapped,

    // IDI-454: keep the camelCase key in step with the snake_case one. The
    // `...coLine` spread above would otherwise leave a stale (often null)
    // `lineTotal` next to a correct `line_total`, and which one a reader
    // picks up decides whether the change order bills its real value or $0.
    lineTotal: mapped.line_total,

    // CO-only fields — explicitly named so any future backend contract
    // negotiation can grep them.
    quote_line_id: coLine.quoteLineId ?? coLine.quote_line_id ?? null,
    alternate_of: coLine.alternateOf ?? coLine.alternate_of ?? null,
    status: coLine.status || 'unchanged',
    locked: Boolean(coLine.locked),
    // `original` is the snapshot of the pre-change quote line. It must
    // survive the round-trip verbatim so CO#2 can seed against #1's applied
    // state without reverting. Preserve as JSON-serialisable object.
    original: coLine.original ?? null,
  };
}

/**
 * Assemble the full CO PUT/POST payload.
 *
 * Extracted so `handleSave` and `handleFinalize` share ONE payload builder —
 * that's the DEF-096 cure. Both callsites must go through this single
 * function; never inline a second copy.
 *
 * @param {object}   params
 * @param {string}   params.coId          Change-order ID (route param).
 * @param {string}   params.jobId         Parent job ID.
 * @param {object[]} params.coLines       Builder-native CO working lines.
 * @param {object}   params.co            CO state ({ netTotal, originalTotal,
 *                                        revisedTotal, ... }) from the
 *                                        change-order engine.
 * @param {string}   params.terms         Terms text.
 * @param {any[]}    params.attachments   Attachment metadata array.
 * @param {string}   params.status        'draft' | 'finalized' | etc.
 * @returns {object}                      Wire payload.
 */
export function assembleChangeOrderPayload({
  coId,
  jobId,
  coLines,
  co,
  terms,
  attachments,
  status,
  // IDI-459: internal notes from the Notes tab. Carried so what the user
  // typed is actually saved (the tab would otherwise discard it on reload).
  internalNotes,
}) {
  // IDI-454: pair each line with its engine-computed counterpart so the
  // serialized price is the real one. Matched by id (the engine preserves
  // it), falling back to position for a line that somehow has none.
  const computedById = new Map();
  const computedLines = Array.isArray(co?.lines) ? co.lines : [];
  computedLines.forEach((cl) => {
    if (cl && cl.id != null) computedById.set(cl.id, cl);
  });
  const lines = Array.isArray(coLines)
    ? coLines.map((l, i) => serializeChangeOrderLine(
      l,
      (l && l.id != null && computedById.get(l.id)) || computedLines[i] || null,
    ))
    : [];
  return {
    id: coId ?? undefined,
    jobId,
    // Envelope key is `lines` (existing contract). Not `line_items`.
    lines,
    // Total surface — preserve all three so the backend has the delta
    // context and downstream readers don't have to recompute.
    netTotal: co?.netTotal ?? 0,
    originalTotal: co?.originalTotal ?? 0,
    revisedTotal: co?.revisedTotal ?? 0,
    terms: terms ?? '',
    attachments: Array.isArray(attachments) ? attachments : [],
    status,
    internalNotes: internalNotes ?? '',
  };
}
